import { useState } from 'react';
import toast from 'react-hot-toast';

import { Gate } from '../../../global/contexts/auth/auth';
import {
    useCreateDeployment,
    useCreateEnvironment,
    useDeleteEnvironment,
    useDeployments,
    useEnvironments,
} from '../contexts/repositories';
import { COMMIT_MAX, COMMIT_MIN, ENVIRONMENT_KIND_OPTIONS, validateDeployment } from '../utils/form';

import '../styles/environments.css';

// A repository's environments, and each environment's deployments.
//
// ONE ENVIRONMENT PER KIND — dev, staging, prod. The picker only offers the
// kinds this repository does not have yet, because a second `prod` is not a
// thing that exists and the API would refuse it.
//
// A DEPLOYMENT IS IMMUTABLE: it is recorded and listed, never edited or
// deleted. Fixing one is recording another, and that is the whole point — the
// history is the record of what actually ran.

const KIND_LABEL = Object.fromEntries(
    ENVIRONMENT_KIND_OPTIONS.map(option => [option.value, option.label]),
);

const DATE_FORMAT = new Intl.DateTimeFormat('es', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

function formatDate(raw) {
    if (!raw) return null;
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? null : DATE_FORMAT.format(date);
}

function Deployments({ environmentId, repositoryId }) {
    const [open, setOpen] = useState(false);
    const { data, isLoading } = useDeployments(environmentId, { enabled: open });
    const createDeployment = useCreateDeployment(environmentId, repositoryId);

    const [values, setValues] = useState({ commit_hash: '', version: '' });
    const [errors, setErrors] = useState({});

    const items = data?.items ?? (Array.isArray(data) ? data : []);

    const handleRecord = async () => {
        const found = validateDeployment(values);
        if (Object.keys(found).length) {
            setErrors(found);
            return;
        }
        try {
            await createDeployment.mutateAsync({
                commit_hash: values.commit_hash.trim().toLowerCase(),
                version: values.version.trim() || null,
            });
            setValues({ commit_hash: '', version: '' });
            setErrors({});
            toast.success('Despliegue registrado');
        } catch (err) {
            if (err.fields) setErrors(err.fields);
            else toast.error(err.message || 'No se pudo registrar');
        }
    };

    return (
        <div className="deployments">
            <button type="button" className="deployments__toggle" onClick={() => setOpen(v => !v)}>
                {open ? 'Ocultar despliegues' : 'Ver despliegues'}
            </button>

            {open && isLoading && <p className="deployments__note">Cargando...</p>}

            {open && !isLoading && (
                <>
                    {!items.length
                        ? <p className="deployments__note">Todavía no se registró ningún despliegue.</p>
                        : (
                            <ul className="deployments__list">
                                {items.map(deployment => (
                                    <li className="deployment" key={deployment.id}>
                                        <code className="deployment__commit">
                                            {String(deployment.commit_hash || '').slice(0, 7)}
                                        </code>
                                        <span className="deployment__version">
                                            {deployment.version || 'sin versión'}
                                        </span>
                                        <span className="deployment__when">
                                            {formatDate(deployment.deployed_at ?? deployment.createdAt) || '—'}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}

                    <Gate domain="repositories:write">
                        <div className="deployments__record">
                            <input
                                className="deployments__input"
                                type="text"
                                value={values.commit_hash}
                                onChange={e => setValues(v => ({ ...v, commit_hash: e.target.value }))}
                                placeholder={`commit (${COMMIT_MIN}-${COMMIT_MAX} hex)`}
                                maxLength={COMMIT_MAX}
                                disabled={createDeployment.isPending}
                            />
                            <input
                                className="deployments__input deployments__input--narrow"
                                type="text"
                                value={values.version}
                                onChange={e => setValues(v => ({ ...v, version: e.target.value }))}
                                placeholder="versión (opcional)"
                                maxLength={60}
                                disabled={createDeployment.isPending}
                            />
                            <button
                                type="button"
                                className="deployments__button"
                                onClick={handleRecord}
                                disabled={createDeployment.isPending}
                            >
                                Registrar
                            </button>
                        </div>
                    </Gate>

                    {errors.commit_hash && (
                        <p className="deployments__error">{errors.commit_hash}</p>
                    )}

                    <p className="deployments__note">
                        Un despliegue no se edita ni se borra: corregir uno es registrar otro.
                        Si trae versión, actualiza la del entorno.
                    </p>
                </>
            )}
        </div>
    );
}

export default function RepositoryEnvironments({ repositoryId }) {
    const { data, isLoading, error } = useEnvironments(repositoryId);
    const createEnvironment = useCreateEnvironment(repositoryId);
    const deleteEnvironment = useDeleteEnvironment(repositoryId);

    const [kind, setKind] = useState('');
    const [url, setUrl] = useState('');

    const environments = data?.items ?? (Array.isArray(data) ? data : []);
    const taken = new Set(environments.map(env => env.kind));
    const available = ENVIRONMENT_KIND_OPTIONS.filter(option => !taken.has(option.value));

    const handleCreate = async () => {
        if (!kind) return;
        try {
            await createEnvironment.mutateAsync({ kind, url: url.trim() || null });
            setKind('');
            setUrl('');
            toast.success('Entorno creado');
        } catch (err) {
            toast.error(err.message || 'No se pudo crear el entorno');
        }
    };

    const handleDelete = async (id) => {
        try {
            await deleteEnvironment.mutateAsync(id);
            toast.success('Entorno desactivado');
        } catch (err) {
            toast.error(err.message || 'No se pudo desactivar');
        }
    };

    if (isLoading) return <p className="environments__loading">Cargando entornos...</p>;

    if (error) {
        return (
            <p className="environments__error">
                {error.message || 'No se pudieron cargar los entornos'}
            </p>
        );
    }

    return (
        <section className="environments">
            <header className="environments__header">
                <h3 className="environments__title">Entornos</h3>
                <span className="environments__count">{environments.length} de 3</span>
            </header>

            {!environments.length && (
                <p className="environments__empty">
                    Este repositorio no tiene entornos. Sin uno, no hay dónde registrar un
                    despliegue.
                </p>
            )}

            {environments.map(environment => (
                <article className="environment" key={environment.id}>
                    <div className="environment__head">
                        <span className={`environment__kind environment__kind--${environment.kind}`}>
                            {KIND_LABEL[environment.kind] ?? environment.kind}
                        </span>

                        {environment.url
                            ? (
                                <a
                                    className="environment__url"
                                    href={environment.url}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    {environment.url}
                                </a>
                            )
                            : <span className="environment__url environment__url--none">Sin URL</span>}

                        <span className="environment__version">
                            {environment.deployed_version || 'sin versión desplegada'}
                        </span>

                        <Gate domain="repositories:admin">
                            <button
                                type="button"
                                className="environment__remove"
                                onClick={() => handleDelete(environment.id)}
                                disabled={deleteEnvironment.isPending}
                            >
                                Desactivar
                            </button>
                        </Gate>
                    </div>

                    <Deployments environmentId={environment.id} repositoryId={repositoryId} />
                </article>
            ))}

            <Gate domain="repositories:write">
                {available.length > 0 && (
                    <div className="environments__add">
                        <select
                            className="environments__select"
                            value={kind}
                            onChange={e => setKind(e.target.value)}
                            disabled={createEnvironment.isPending}
                        >
                            <option value="">Agregar entorno...</option>
                            {available.map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>

                        <input
                            className="environments__input"
                            type="url"
                            value={url}
                            onChange={e => setUrl(e.target.value)}
                            placeholder="https://… (opcional)"
                            disabled={createEnvironment.isPending}
                        />

                        <button
                            type="button"
                            className="environments__button"
                            onClick={handleCreate}
                            disabled={!kind || createEnvironment.isPending}
                        >
                            Crear
                        </button>
                    </div>
                )}

                {!available.length && (
                    <p className="environments__note">
                        Los tres entornos existen. Hay uno por tipo y no puede haber dos.
                    </p>
                )}
            </Gate>
        </section>
    );
}
